import { useMutation, useQuery } from "@apollo/client";
import { Button, ButtonGroup, Card, Intent, Tag } from "@blueprintjs/core";
import { useState } from "react";
import {
  CreateFootprintInput,
  Footprint,
  Footprint_Type,
  UpdateFootprintInput,
} from "../../api/schema";
import {
  FlexContainer,
  FlexGrow,
  FlexRowStretch,
} from "../../components/flex/Flex";
import {
  DeleteModal,
  DeleteStateHelper,
} from "../../components/form/DeleteModal";
import { ModalInputType } from "../../components/form/Form";
import {
  InputFormMode,
  UpdateStateHelper,
  ValuesVariables,
} from "../../components/form/InputForm";
import { InputModal } from "../../components/form/InputModal";
import { ListView } from "../../components/query/ListView";
import { Spinner } from "../../components/Spinner";
import { UserLanguage } from "../../lang/lang";
import {
  CreateFootprintMutation,
  CreateFootprintResponse,
  DeleteFootprintMutation,
  DeleteFootprintResponse,
  FootprintListQuery,
  FootprintListResponse,
  UpdateFootprintMutation,
  UpdateFootprintResponse,
} from "./graphql";

export const FootprintTypeIntent: { [key in Footprint_Type]: Intent } = {
  [Footprint_Type.SMD]: Intent.SUCCESS,
  [Footprint_Type.THT]: Intent.WARNING,
  [Footprint_Type.None]: Intent.PRIMARY,
};

export const FootprintListView = (props: any) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalState, setDeleteModalState] = useState<DeleteStateHelper>({
    open: false,
    id: undefined,
    prettyPrint: undefined,
  });
  const [updateFootprintState, setUpdateFootprintState] = useState<
    UpdateStateHelper<UpdateFootprintInput>
  >({ open: false, item: undefined });

  const [createFootprint] = useMutation<
    CreateFootprintResponse,
    ValuesVariables<CreateFootprintInput>
  >(CreateFootprintMutation);
  const [updateFootprint] = useMutation<
    UpdateFootprintResponse,
    ValuesVariables<UpdateFootprintInput>
  >(UpdateFootprintMutation);
  const [deleteFootprint] = useMutation<DeleteFootprintResponse>(
    DeleteFootprintMutation
  );

  const listQuery = useQuery<FootprintListResponse>(FootprintListQuery, {
    fetchPolicy: "no-cache",
  });

  return (
    <FlexContainer>
      <Card elevation={3} style={{ margin: 25 }}>
        <FlexRowStretch>
          <div>
            <h2>{UserLanguage().pages.footprint.list}</h2>
          </div>
          <FlexGrow />
          <div>
            <Button
              intent="success"
              icon="plus"
              onClick={() => setCreateModalOpen(true)}
            >
              {UserLanguage().actions.create}
            </Button>
          </div>
        </FlexRowStretch>
        <InputModal<
          CreateFootprintInput,
          CreateFootprintResponse,
          CreateFootprintInput
        >
          keys={{
            name: {
              type: ModalInputType.String,
              icon: "id-number",
              label: UserLanguage().data.name,
            },
            type: {
              type: ModalInputType.ItemSelect,
              items: [
                Footprint_Type.SMD,
                Footprint_Type.THT,
                Footprint_Type.None,
              ],
              label: UserLanguage().data.footprint.type,
              placeholder: UserLanguage().actions.select(
                UserLanguage().data.footprint.type
              ),
            },
          }}
          open={createModalOpen}
          texts={UserLanguage().pages.footprint.actions}
          onClose={() => setCreateModalOpen(false)}
          mutation={createFootprint}
          onSuccess={() => listQuery.refetch()}
          resetOnClose
          mode={InputFormMode.Create}
        />
        <InputModal<
          UpdateFootprintInput,
          UpdateFootprintResponse,
          UpdateFootprintInput
        >
          keys={{
            id: {
              type: ModalInputType.String,
              readonly: true,
              label: UserLanguage().data.id,
            },
            name: {
              type: ModalInputType.String,
              icon: "id-number",
              label: UserLanguage().data.name,
            },
            type: {
              type: ModalInputType.ItemSelect,
              items: [
                Footprint_Type.SMD,
                Footprint_Type.THT,
                Footprint_Type.None,
              ],
              label: UserLanguage().data.footprint.type,
              placeholder: UserLanguage().actions.select(
                UserLanguage().data.footprint.type
              ),
            },
          }}
          open={updateFootprintState.open}
          input={updateFootprintState.item}
          texts={UserLanguage().pages.footprint.actions}
          onClose={() =>
            setUpdateFootprintState({ open: false, item: undefined })
          }
          mutation={updateFootprint}
          onSuccess={() => {
            listQuery.refetch();
            setUpdateFootprintState({ open: false, item: undefined });
          }}
          resetOnClose
          mode={InputFormMode.Edit}
        />
        <DeleteModal<DeleteFootprintResponse>
          id={deleteModalState.id}
          open={deleteModalState.open}
          mutation={(id) => deleteFootprint({ variables: { id } })}
          texts={UserLanguage().pages.footprint.actions}
          resetOnClose
          onClose={() => {
            setDeleteModalState({ open: false, id: undefined });
          }}
          onSuccess={() => {
            setDeleteModalState({ open: false, id: undefined });
            listQuery.refetch();
          }}
          type={UserLanguage().data.footprint.label}
          prettyPrint={deleteModalState.prettyPrint}
        />
        <ListView<FootprintListResponse, Footprint>
          cols={{
            id: { label: UserLanguage().data.id },
            name: { label: UserLanguage().data.name },
            type: {
              label: UserLanguage().data.footprint.type,
              render: (i) => (
                <Tag large intent={FootprintTypeIntent[i.type]}>
                  {i.type}
                </Tag>
              ),
            },
            actions: {
              label: UserLanguage().actions.label,
              render: (item) => (
                <ButtonGroup>
                  <Button
                    intent="primary"
                    rightIcon="edit"
                    onClick={() =>
                      setUpdateFootprintState({
                        open: true,
                        item: item as UpdateFootprintInput,
                      })
                    }
                  >
                    {UserLanguage().actions.edit}
                  </Button>
                  <Button
                    intent="danger"
                    rightIcon="delete"
                    onClick={() =>
                      setDeleteModalState({
                        open: true,
                        id: item.id,
                        prettyPrint: `${UserLanguage().data.name}: ${
                          item.name
                        }`,
                      })
                    }
                  >
                    {UserLanguage().actions.delete}
                  </Button>
                </ButtonGroup>
              ),
            },
          }}
          error={<p>Error</p>}
          loading={<Spinner />}
          itemKey="footprints"
          query={listQuery}
        />
      </Card>
    </FlexContainer>
  );
};
